// Consumo del comedor (Fase 2). Staff: asistencia real vs planificada por servicio,
// registro/anulación manual y QR de la pantalla de comedor. Catering → Operación.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { ScanLine, Check, Loader2, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { cat, catRpc, invalidateCatering, SERVICIO_LABEL } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Contrato { id: string; nombre: string; modo_servicio: 'bloque' | 'comensal' }
interface Fila { comensal_id: string; codigo: string | null; nombre: string; consumido: boolean; hora: string | null; origen: string | null }
interface Resumen { plan: number; real: number; padron: number; comensales: Fila[] }

const SERVICIOS = Object.keys(SERVICIO_LABEL);
const isoHoy = (): string => new Date().toISOString().slice(0, 10);
const hhmm = (iso: string | null): string => iso ? new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' }) : '';

export function ConsumoCatering() {
  const qc = useQueryClient();
  const [contratoId, setContratoId] = useState('');
  const [fecha, setFecha] = useState(isoHoy());
  const [servicio, setServicio] = useState('almuerzo_1');

  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['cat-contratos-consumo'],
    queryFn: () => cat.list<Contrato>('contratos', '?select=id,nombre,modo_servicio&es_activo=is.true&deleted_at=is.null&order=nombre'),
  });
  const contrato = contratos.find((c) => c.id === contratoId) ?? contratos[0];
  const cid = contrato?.id ?? '';

  const { data, isLoading } = useQuery<Resumen>({
    queryKey: ['cat-consumo-resumen', cid, fecha, servicio],
    queryFn: () => catRpc<Resumen>('cat_consumo_resumen', { _contrato_id: cid, _fecha: fecha, _servicio: servicio }),
    enabled: !!cid,
  });
  const { data: kiosco } = useQuery<{ token: string }>({
    queryKey: ['cat-kiosco-token', cid],
    queryFn: () => catRpc<{ token: string }>('cat_kiosco_token', { _contrato_id: cid }),
    enabled: !!cid,
  });

  const toggle = useMutation({
    mutationFn: (f: Fila) => f.consumido
      ? catRpc('cat_consumo_anular', { _comensal_id: f.comensal_id, _fecha: fecha, _servicio: servicio })
      : catRpc('cat_consumo_registrar', { _comensal_id: f.comensal_id, _fecha: fecha, _servicio: servicio, _origen: 'staff' }),
    onSuccess: () => { invalidateCatering(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pct = data && data.plan > 0 ? Math.round((100 * data.real) / data.plan) : null;
  const comedorUrl = kiosco ? `${window.location.origin}/comedor/${kiosco.token}` : '';

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><ScanLine className="h-4 w-4" /> Consumo del comedor</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Contrato</label>
              <select className={`${fieldCls} w-72`} value={cid} onChange={(e) => setContratoId(e.target.value)}>
                {contratos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div><label className="block text-xs text-muted-foreground">Fecha</label><input type="date" className={`${fieldCls} w-40`} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Servicio</label>
              <select className={`${fieldCls} w-40`} value={servicio} onChange={(e) => setServicio(e.target.value)}>
                {SERVICIOS.map((s) => <option key={s} value={s}>{SERVICIO_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-4 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}
          {!isLoading && data && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Planificado (pax)</p><p className="text-xl font-semibold">{num(data.plan, 0)}</p></div>
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Consumo real</p><p className="text-xl font-semibold">{num(data.real, 0)}</p></div>
              <div className={`rounded-md border p-2 ${pct != null && pct > 100 ? 'border-red-400' : ''}`}><p className="text-[11px] uppercase text-muted-foreground">Asistencia</p><p className="text-xl font-semibold">{pct != null ? `${pct}%` : '—'}</p></div>
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Padrón</p><p className="text-xl font-semibold">{num(data.padron, 0)}</p><p className="text-[11px] text-muted-foreground">comensales</p></div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Padrón del servicio</CardTitle>
            <p className="text-xs text-muted-foreground">Marca/desmarca el consumo manualmente, o usa la pantalla de comedor con el badge.</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Nombre</th><th className="py-1 pr-2">Hora</th><th className="py-1 pr-2">Origen</th><th className="py-1 pr-2 text-center">Consumió</th></tr></thead>
                <tbody>
                  {(data?.comensales ?? []).map((f) => (
                    <tr key={f.comensal_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 font-mono text-xs">{f.codigo ?? '—'}</td>
                      <td className="py-1.5 pr-2 font-medium">{f.nombre}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{hhmm(f.hora)}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{f.origen ?? ''}</td>
                      <td className="py-1.5 pr-2 text-center">
                        <button disabled={toggle.isPending} onClick={() => toggle.mutate(f)}
                          className={`inline-flex h-7 w-7 items-center justify-center rounded-full border-2 ${f.consumido ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 text-transparent hover:border-emerald-400'}`}>
                          <Check className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!data?.comensales?.length && <tr><td colSpan={5} className="py-3 text-sm text-muted-foreground">Sin comensales en el padrón de este contrato. Cárgalos en Comensales.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="h-fit">
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><QrCode className="h-4 w-4" /> Pantalla de comedor</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center gap-2">
            {comedorUrl ? (
              <>
                <div className="rounded-md bg-white p-2"><QRCodeSVG value={comedorUrl} size={140} /></div>
                <a className="break-all text-center text-xs text-primary underline" href={comedorUrl} target="_blank" rel="noreferrer">{comedorUrl}</a>
                <p className="text-center text-[11px] text-muted-foreground">Abre esta pantalla en una tablet a la entrada del comedor.</p>
              </>
            ) : <p className="text-sm text-muted-foreground">Selecciona un contrato.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
